import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { mockBadges } from '../../data/mockData';
import { Badge, Member } from '../../types';
import RolePill from '../UI/RolePill';
import QRCodePrintModal from './QRCodePrintModal';
import './Modal.css';
import AvatarImage from '../UI/AvatarImage';
import { translateRoles, translateRole, normalizeRoleKey } from '../../utils/roleTranslations';
import { translateSkill, translateSubSkill, SKILLS_FR, SUB_SKILLS_FR } from '../../translations/skills';
import { getLocalBadgeImage } from '../../utils/badgeImages';
import { getBadgeLevelDisplayLabel } from '../../utils/badgeLevelLabels';
import CompactProgressBadge from '../Badges/CompactProgressBadge';
import { isSeriesWithCompetenceProgress } from '../../constants/badgeAxes';
import {
  listSchoolParentLinkCodes,
  regenerateSchoolParentLinkCode,
  SchoolParentActiveLink,
  SchoolParentLinkCode,
} from '../../api/SchoolParentLinkCodes';
import {
  listSchoolStudentLoginCodes,
  regenerateSchoolStudentLoginCode,
  SchoolStudentLoginCode,
} from '../../api/SchoolStudentLoginCodes';
import {
  sendStudentGuardianInvitation,
  updateStudentGuardianEmail,
} from '../../api/SchoolStudentGuardian';
import {
  getSchoolPersonalKeyStatus,
  printSchoolPersonalKey,
  SchoolPersonalKeyStatus,
} from '../../api/SchoolPersonalKey';
import { resendCompanyGuardianAuthorization } from '../../api/CompanyDashboard/Members';

const FEATURE_PIK_REMISE = process.env.REACT_APP_FEATURE_PIK_REMISE === 'true';

function isUnderDigitalMajority(birthday?: string | null): boolean {
  if (!birthday) return false;
  const dob = new Date(birthday);
  if (Number.isNaN(dob.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age < 15;
}

/** Parse API date-only (YYYY-MM-DD) as local calendar day — avoid UTC-midnight skew. */
function parseApiDateParts(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  return { y: Number(match[1]), m: Number(match[2]) - 1, d: Number(match[3]) };
}

function formatFrDate(iso?: string | null): string {
  if (!iso) return '—';
  const parts = parseApiDateParts(iso);
  if (parts) {
    return new Date(parts.y, parts.m, parts.d).toLocaleDateString('fr-FR');
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

/** Parental claim date-only is valid through the end of that local calendar day. */
function isParentalClaimActive(iso?: string | null): boolean {
  if (!iso) return false;
  const parts = parseApiDateParts(iso);
  if (parts) {
    const endOfDay = new Date(parts.y, parts.m, parts.d, 23, 59, 59, 999);
    return endOfDay.getTime() >= Date.now();
  }
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && t > Date.now();
}

type FoldableSectionProps = {
  title: string;
  isOpen: boolean;
  emptySuffix?: string;
  children?: React.ReactNode;
  collapsedContent?: React.ReactNode;
};

const FoldableSection: React.FC<FoldableSectionProps> = ({
  title,
  isOpen,
  emptySuffix = 'aucune',
  children,
  collapsedContent,
}) => (
  <div className={`info-section${isOpen ? '' : ' info-section--folded'}`}>
    <h3>{isOpen ? title : `${title} — ${emptySuffix}`}</h3>
    {isOpen ? children : collapsedContent}
  </div>
);

interface MemberModalProps {
  member: Member;
  onClose: () => void;
  onUpdate: (updates: Partial<Member>) => void;
  onDelete: () => void;
  onContactClick: () => void;
  hideDeleteButton?: boolean; // Option to hide delete button and permissions (for network members)
  hideEditButton?: boolean; // Option to hide edit button (for network members)
  isSuperadmin?: boolean; // Hide delete button for superadmins
  badgeCartographyUrl?: string; // Optional URL for badge cartography
  hasBadges?: boolean; // When true, show Cartographie entry even while URL is loading (Élèves tab)
  isCartographyLoading?: boolean; // When true, show "Cartographie (chargement…)" instead of link
  hideContactAndEmail?: boolean; // When true (e.g. viewer is personal user under 15 in Mon réseau), hide contact actions and email in modal
  schoolId?: number; // School context — enables Parent-Link codes on student card
  companyId?: number; // Company context — BLEU Premium <15 guardian authorization card
}

const MemberModal: React.FC<MemberModalProps> = ({
  member,
  onClose,
  onUpdate,
  onDelete,
  onContactClick,
  hideDeleteButton = false,
  hideEditButton = false,
  isSuperadmin = false,
  badgeCartographyUrl,
  hasBadges = false,
  isCartographyLoading = false,
  hideContactAndEmail = false,
  schoolId,
  companyId
}) => {
  const { state } = useAppContext();
  const displayRoles = translateRoles(member.roles);
  // Profession should be the actual job, not translated system role
  const professionLabel = member.profession || '';

  const [parentCodes, setParentCodes] = useState<SchoolParentLinkCode[]>([]);
  const [parentLinks, setParentLinks] = useState<SchoolParentActiveLink[]>([]);
  const [parentCodesLoading, setParentCodesLoading] = useState(false);
  const [parentCodesError, setParentCodesError] = useState('');
  const [regeneratingCode, setRegeneratingCode] = useState(false);
  const [studentLoginCodes, setStudentLoginCodes] = useState<SchoolStudentLoginCode[]>([]);
  const [studentLoginCodesLoading, setStudentLoginCodesLoading] = useState(false);
  const [studentLoginCodesError, setStudentLoginCodesError] = useState('');
  const [regeneratingStudentCode, setRegeneratingStudentCode] = useState(false);
  const [revealedStudentCode, setRevealedStudentCode] = useState<string | null>(null);
  const [guardianEmailDraft, setGuardianEmailDraft] = useState(member.guardianEmail || '');
  const [guardianSaving, setGuardianSaving] = useState(false);
  const [guardianInviting, setGuardianInviting] = useState(false);
  const [guardianMessage, setGuardianMessage] = useState('');
  const [guardianError, setGuardianError] = useState('');
  const [consentResending, setConsentResending] = useState(false);
  const [consentMessage, setConsentMessage] = useState('');
  const [consentError, setConsentError] = useState('');
  const [pikStatus, setPikStatus] = useState<SchoolPersonalKeyStatus | null>(null);
  const [pikLoading, setPikLoading] = useState(false);
  const [pikError, setPikError] = useState('');
  const [pikPrinting, setPikPrinting] = useState(false);
  const [revealedParentCodeIds, setRevealedParentCodeIds] = useState<Set<number>>(new Set());

  // Helper function to translate skill (tries main skill first, then sub-skill)
  const translateSkillName = (skillName: string): string => {
    const translated = translateSkill(skillName);
    // If translation found (different from original), return it
    if (translated !== skillName) {
      return translated;
    }
    // Otherwise try sub-skill translation
    return translateSubSkill(skillName);
  };

  // Helper function to separate and organize skills
  const organizeSkills = (skills: string[]) => {
    const mainSkillsSet = new Set<string>();
    const subSkills: string[] = [];

    skills.forEach(skill => {
      // Check if it's a main skill
      if (SKILLS_FR[skill]) {
        mainSkillsSet.add(skill);
      } 
      // Check if it's a sub-skill
      else if (SUB_SKILLS_FR[skill]) {
        subSkills.push(skill);
      }
      // If not in either, treat as main skill (fallback)
      else {
        mainSkillsSet.add(skill);
      }
    });

    return {
      mainSkills: Array.from(mainSkillsSet),
      subSkills: subSkills
    };
  };

  // Helper functions for badge data
  const getLevelName = (level: string): string => {
    const levelNames: { [key: string]: string } = {
      '1': 'Découverte',
      '2': 'Application',
      '3': 'Maîtrise',
      '4': 'Expertise'
    };
    return levelNames[level] || 'Découverte';
  };

  const getBadgeDescription = (title: string, series: string, level: string): string => {
    // Common descriptions for TouKouLeur badges
    const descriptions: { [key: string]: string } = {
      'Adaptabilité': 'CAPACITÉ À PRENDRE EN COMPTE LES CONTRAINTES, LES ALÉAS ET LES OPPORTUNITÉS CONTEXTUELS DANS LA POURSUITE D\'UN OBJECTIF.',
      'Créativité': 'CAPACITÉ À PROPOSER DE NOUVELLES SOLUTIONS, DES APPROCHES ORIGINALES ET DES INNOVATIONS POUR RÉSOUDRE DES PROBLÈMES COMPLEXES.',
      'Communication': 'CAPACITÉ À TRANSMETTRE DES INFORMATIONS DE MANIÈRE CLAIRE, PRÉCISE ET ADAPTÉE AU CONTEXTE ET AUX INTERLOCUTEURS.',
      'Coopération': 'CAPACITÉ À TRAVAILLER EN ÉQUIPE, À COLLABORER EFFICACEMENT ET À CONTRIBUER À L\'ATTEINTE D\'OBJECTIFS COLLECTIFS.',
      'Engagement': 'CAPACITÉ À S\'INVESTIR PLEINEMENT DANS UNE ACTIVITÉ, UN PROJET OU UNE CAUSE AVEC DÉTERMINATION ET PERSÉVÉRANCE.',
      'Esprit Critique': 'CAPACITÉ À ANALYSER, ÉVALUER ET QUESTIONNER LES INFORMATIONS, LES ARGUMENTS ET LES SITUATIONS DE MANIÈRE OBJECTIVE.',
      'Formation': 'CAPACITÉ À ACQUÉRIR DE NOUVELLES CONNAISSANCES ET COMPÉTENCES DE MANIÈRE AUTONOME ET CONTINUE.',
      'Gestion de Projet': 'CAPACITÉ À PLANIFIER, ORGANISER ET PILOTER UN PROJET DE SA CONCEPTION À SA RÉALISATION.',
      'Information Numérique': 'CAPACITÉ À UTILISER LES OUTILS NUMÉRIQUES DE MANIÈRE EFFICACE ET RESPONSABLE POUR RECHERCHER, TRAITER ET PARTAGER DES INFORMATIONS.',
      'Organisation Opérationnelle': 'CAPACITÉ À ORGANISER ET OPTIMISER LES PROCESSUS ET LES RESSOURCES POUR ATTEINDRE DES OBJECTIFS EFFICACEMENT.',
      'Sociabilité': 'CAPACITÉ À ÉTABLIR ET MAINTENIR DES RELATIONS POSITIVES AVEC AUTRUI DANS DIFFÉRENTS CONTEXTES SOCIAUX.'
    };
    return descriptions[title] || 'PREUVE ATTRIBUÉE POUR RECONNAÎTRE LES COMPÉTENCES DÉVELOPPÉES DANS CE DOMAINE.';
  };
  // const [showAllBadges, setShowAllBadges] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ [key: string]: boolean }>({});
  const [showAllBadgeGroups, setShowAllBadgeGroups] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [editedMember, setEditedMember] = useState({
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
    profession: member.profession,
    skills: [...member.skills],
    availability: [...member.availability]
  });
  const [proposals, setProposals] = useState({
    canProposeStage: member.canProposeStage || false,
    canProposeAtelier: member.canProposeAtelier || false
  });

  // Update proposals when member prop changes
  useEffect(() => {
    setProposals({
      canProposeStage: member.canProposeStage || false,
      canProposeAtelier: member.canProposeAtelier || false
    });
  }, [member.canProposeStage, member.canProposeAtelier]);

  // const BADGES_LIMIT = 6; // Show only 6 badges initially

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'role-admin';
      case 'Référent': return 'role-referent';
      case 'Membre': return 'role-membre';
      case 'Intervenant': return 'role-intervenant';
      default: return 'role-membre';
    }
  };

  // Check if member is a student (élève)
  const isStudent = () => {
    const roleCandidates = [
      ...(member.roles || []),
      member.role,
      (member as any).systemRole,
      (member as any).rawRole
    ].filter(Boolean) as string[];

    const isRoleStudent = roleCandidates.some(role => {
      const normalized = normalizeRoleKey(role);
      return (
        normalized.includes('eleve') ||
        normalized.includes('student') ||
        normalized.includes('etudiant') ||
        normalized.includes('collegien') ||
        normalized.includes('lyceen')
      );
    });

    return isRoleStudent || member.hasTemporaryEmail;
  };

  const studentCanPrintKey = member.hasTemporaryEmail || !member.confirmedAt;
  const pikSectionOpen = studentCanPrintKey;

  const loadPersonalKeyStatus = useCallback(async () => {
    if (!FEATURE_PIK_REMISE || !schoolId || !isStudent()) return;
    setPikLoading(true);
    setPikError('');
    try {
      const res = await getSchoolPersonalKeyStatus(schoolId, member.id);
      setPikStatus(res.data.data);
    } catch (err: any) {
      setPikError(
        err?.response?.data?.message || err.message || 'Impossible de charger le statut de la clé'
      );
      setPikStatus(null);
    } finally {
      setPikLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, member.id]);

  const loadParentLinkCodes = useCallback(async () => {
    if (!schoolId || !isStudent()) return;
    setParentCodesLoading(true);
    setParentCodesError('');
    try {
      const res = await listSchoolParentLinkCodes(schoolId, member.id);
      setParentCodes(res.data.data?.codes || []);
      setParentLinks(res.data.data?.active_links || []);
    } catch (err: any) {
      setParentCodesError(
        err?.response?.data?.message || err.message || 'Impossible de charger les codes Parent-Link'
      );
      setParentCodes([]);
      setParentLinks([]);
    } finally {
      setParentCodesLoading(false);
    }
    // isStudent depends on member fields already covered by member.id / roles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, member.id]);

  const loadStudentLoginCodes = useCallback(async () => {
    if (!schoolId || !isStudent()) return;
    setStudentLoginCodesLoading(true);
    setStudentLoginCodesError('');
    try {
      const res = await listSchoolStudentLoginCodes(schoolId, member.id);
      setStudentLoginCodes(res.data.data?.codes || []);
    } catch (err: any) {
      setStudentLoginCodesError(
        err?.response?.data?.message || err.message || 'Impossible de charger les codes élève'
      );
      setStudentLoginCodes([]);
    } finally {
      setStudentLoginCodesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, member.id]);

  useEffect(() => {
    void loadParentLinkCodes();
  }, [loadParentLinkCodes]);

  useEffect(() => {
    void loadStudentLoginCodes();
    setRevealedStudentCode(null);
  }, [loadStudentLoginCodes]);

  useEffect(() => {
    void loadPersonalKeyStatus();
  }, [loadPersonalKeyStatus]);

  useEffect(() => {
    setGuardianEmailDraft(member.pendingGuardianEmail || member.guardianEmail || '');
  }, [member.guardianEmail, member.pendingGuardianEmail, member.id]);

  const hasPendingGuardianEmail = Boolean(member.pendingGuardianEmail?.trim());
  const savedGuardianForInvite = (
    member.pendingGuardianEmail ||
    member.guardianEmail ||
    ''
  ).trim();
  const guardianDraftMatchesSaved =
    guardianEmailDraft.trim() !== '' &&
    guardianEmailDraft.trim() === savedGuardianForInvite;
  const inviteButtonLabel = hasPendingGuardianEmail
    ? "Prévenir l'adresse actuelle et appliquer"
    : "Envoyer l'invitation";

  const handleSaveGuardianEmail = async () => {
    if (!schoolId) return;
    const next = guardianEmailDraft.trim();
    if (!next) {
      setGuardianError('Indiquez une adresse email.');
      return;
    }
    setGuardianSaving(true);
    setGuardianError('');
    setGuardianMessage('');
    try {
      const res = await updateStudentGuardianEmail(schoolId, member.id, next);
      const payload = res.data.data;
      const pending = payload.pending_guardian_email;
      const saved = payload.guardian_email;
      setGuardianEmailDraft(pending || saved || '');
      onUpdate({
        guardianEmail: saved,
        pendingGuardianEmail: pending,
      });
      setGuardianMessage(
        pending
          ? 'Nouvelle adresse enregistrée en attente. L’invitation partira à l’adresse encore en base ; la nouvelle sera appliquée après envoi.'
          : 'Adresse enregistrée. Aucune invitation n’a été envoyée.'
      );
    } catch (err: any) {
      setGuardianError(
        err?.response?.data?.message || err.message || 'Enregistrement impossible'
      );
    } finally {
      setGuardianSaving(false);
    }
  };

  const handleSendGuardianInvitation = async () => {
    if (!schoolId) return;
    const wasPendingReplace = hasPendingGuardianEmail;
    setGuardianInviting(true);
    setGuardianError('');
    setGuardianMessage('');
    try {
      const res = await sendStudentGuardianInvitation(schoolId, member.id);
      const payload = res.data.data;
      setGuardianEmailDraft(payload.guardian_email || '');
      onUpdate({
        guardianEmail: payload.guardian_email,
        pendingGuardianEmail: payload.pending_guardian_email,
      });
      setGuardianMessage(
        wasPendingReplace
          ? 'Invitation envoyée à l’adresse actuelle ; nouvelle adresse appliquée.'
          : 'Invitation envoyée.'
      );
    } catch (err: any) {
      setGuardianError(
        err?.response?.data?.message || err.message || 'Envoi impossible'
      );
    } finally {
      setGuardianInviting(false);
    }
  };

  const handlePrintPersonalKey = async () => {
    if (!schoolId) return;
    setPikPrinting(true);
    setPikError('');
    try {
      await printSchoolPersonalKey(schoolId, member.id);
      await loadPersonalKeyStatus();
    } catch (err: any) {
      setPikError(err?.message || 'Impression impossible');
    } finally {
      setPikPrinting(false);
    }
  };

  const toggleParentCodeVisibility = (codeId: number) => {
    setRevealedParentCodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(codeId)) {
        next.delete(codeId);
      } else {
        next.add(codeId);
      }
      return next;
    });
  };

  const formatPikDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('fr-FR');
  };

  const handleRegenerateParentCode = async () => {
    if (!schoolId) return;
    setRegeneratingCode(true);
    setParentCodesError('');
    try {
      await regenerateSchoolParentLinkCode(schoolId, member.id);
      await loadParentLinkCodes();
    } catch (err: any) {
      setParentCodesError(err?.message || 'Régénération impossible');
    } finally {
      setRegeneratingCode(false);
    }
  };

  const handleRegenerateStudentLoginCode = async () => {
    if (!schoolId) return;
    setRegeneratingStudentCode(true);
    setStudentLoginCodesError('');
    setRevealedStudentCode(null);
    try {
      const { code } = await regenerateSchoolStudentLoginCode(schoolId, member.id);
      setRevealedStudentCode(code);
      await loadStudentLoginCodes();
    } catch (err: any) {
      setStudentLoginCodesError(
        err?.response?.data?.message || err?.message || 'Régénération impossible'
      );
    } finally {
      setRegeneratingStudentCode(false);
    }
  };

  const toggleDescriptionExpansion = (badgeKey: string) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [badgeKey]: !prev[badgeKey]
    }));
  };

  const handleProposalChange = (proposal: keyof typeof proposals) => {
    setProposals(prev => ({
      ...prev,
      [proposal]: !prev[proposal]
    }));
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset to original values when canceling
      setEditedMember({
        firstName: member.firstName,
        lastName: member.lastName,
        email: member.email,
        profession: member.profession,
        skills: [...member.skills],
        availability: [...member.availability]
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    onUpdate({
      firstName: editedMember.firstName,
      lastName: editedMember.lastName,
      email: editedMember.email,
      profession: editedMember.profession,
      skills: editedMember.skills,
      availability: editedMember.availability,
      canProposeStage: proposals.canProposeStage,
      canProposeAtelier: proposals.canProposeAtelier
    });
    setIsEditing(false);
  };

  const handleFieldChange = (field: string, value: string) => {
    setEditedMember(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddSkill = () => {
    const newSkill = prompt('Ajouter une compétence:');
    if (newSkill && newSkill.trim() && !editedMember.skills.includes(newSkill.trim())) {
      setEditedMember(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    setEditedMember(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleAddAvailability = () => {
    const newAvailability = prompt('Ajouter une disponibilité:');
    if (newAvailability && newAvailability.trim() && !editedMember.availability.includes(newAvailability.trim())) {
      setEditedMember(prev => ({
        ...prev,
        availability: [...prev.availability, newAvailability.trim()]
      }));
    }
  };

  const handleRemoveAvailability = (availabilityToRemove: string) => {
    setEditedMember(prev => ({
      ...prev,
      availability: prev.availability.filter(avail => avail !== availabilityToRemove)
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content member-management-content" onClick={(e) => e.stopPropagation()}>

        <div className="flex relative gap-4 items-start modal-header">
        <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
          {/* block avatar , trusted badge */}
          <div className="flex relative justify-start items-center">
            <div className="border-2 member-avatar-large border-primary">
            <AvatarImage src={member.avatar} className="object-cover w-full h-full" alt={`${member.firstName} ${member.lastName}`} />
            </div>
            {member.isTrusted && (
              <div className="right-0 top-10 trusted-badge-large">
                <i className="fas fa-shield-alt"></i>
              </div>
            )}
          </div>
          {/* block name, profession, roles */}
          <div className="flex flex-col gap-2 member-details">
            <h2 className="member-name">{member.firstName} {member.lastName}</h2>
            <p className="">{professionLabel}</p>
            <div className="">
              {displayRoles.map((role, index) => (
                <RolePill key={index} role={role} color={getRoleColor(role)} />
              ))}
            </div>

          </div>
          {/* block actions */}
          <div className="flex flex-wrap gap-2 items-center">
            {isStudent() && member.claim_token && (
              <button
                className="btn btn-outline btn-sm"
                onClick={() => {
                  setShowQRCodeModal(true)
                }}
                title="Afficher le QR Code d'activation"
              >
                <i className="fas fa-qrcode"></i>
                QR Code
              </button>
            )}
            {isStudent() && (badgeCartographyUrl || hasBadges) && (
              badgeCartographyUrl ? (
                <a
                  href={badgeCartographyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  title="Voir la cartographie des preuves de compétences"
                >
                  <i className="fas fa-map"></i>
                  Cartographie
                </a>
              ) : (
                <span
                  className="btn btn-outline btn-sm"
                  title="Voir la cartographie des preuves de compétences"
                  style={{ opacity: 0.8, cursor: 'default' }}
                >
                  <i className="fas fa-map"></i>
                  Cartographie (chargement…)
                </span>
              )
            )}
            {!hideContactAndEmail && (hideDeleteButton ? (
              <button 
                className="btn btn-outline btn-sm" 
                onClick={async () => {
                  if (member.email) {
                    try {
                      await navigator.clipboard.writeText(member.email);
                      alert('Email copié dans le presse-papiers');
                    } catch (err) {
                      console.error('Failed to copy email:', err);
                      // Fallback for older browsers
                      const textArea = document.createElement('textarea');
                      textArea.value = member.email;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      alert('Email copié dans le presse-papiers');
                    }
                  }
                }}
              >
                <i className="fas fa-copy"></i>
                Copier l'email
              </button>
            ) : (
              <a
              href={`mailto:${member.email}`}
              className="btn btn-outline btn-sm"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <i className="fas fa-envelope"></i>
              Contacter
            </a>
            ))}
            {!hideDeleteButton && !isSuperadmin && (
              <button className="btn btn-outline btn-sm" onClick={onDelete}>
                <i className="fas fa-trash"></i>
                Supprimer
              </button>
            )}
          </div>


        </div>
        {showQRCodeModal && member.claim_token ? (
          <div className="modal-body">
            <QRCodePrintModal
              onClose={() => setShowQRCodeModal(false)}
              claimToken={member.claim_token || ''}
              studentName={member.fullName || `${member.firstName} ${member.lastName}`}
            />
          </div>
        ) : (
          <>
            <div className="modal-body">
              <div className="member-info-grid">
                <div className="info-section">
                  <h3>Informations personnelles</h3>
                  <div className="info-item">
                    <label>Prénom:</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedMember.firstName}
                        onChange={(e) => handleFieldChange('firstName', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      <span>{member.firstName}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Nom:</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedMember.lastName}
                        onChange={(e) => handleFieldChange('lastName', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      <span>{member.lastName}</span>
                    )}
                  </div>
                  {!hideContactAndEmail && (
                  <div className="info-item">
                    <label>Email:</label>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editedMember.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      <span>{member.email}</span>
                    )}
                  </div>
                  )}
                  <div className="info-item">
                    <label>Profession:</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedMember.profession}
                        onChange={(e) => handleFieldChange('profession', e.target.value)}
                        className="edit-input"
                      />
                    ) : (
                      <span>{professionLabel}</span>
                    )}
                  </div>
                  {isStudent() && member.classes && member.classes.length > 0 && (
                    <div className="info-item">
                      <label>Classe:</label>
                      <span>
                        {member.classes.map((c) => c.name).filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                {/* BLEU Premium <15 — autorisation représentant légal (carte membre, Lot 6).
                    Gate on companyId only — do not use !isStudent(): hasTemporaryEmail would hide
                    company minors who still use a temporary email. */}
                {companyId && isUnderDigitalMajority(member.birthday) && member.legalRepresentativeConsentGivenAt && (
                  <div className="info-section">
                    <h3>Autorisation du représentant légal</h3>
                    {isParentalClaimActive(member.parentalClaimValidUntil) ? (
                      <>
                        <div className="info-item">
                          <label>Statut :</label>
                          <span>Accordée</span>
                        </div>
                        <p className="no-badges" style={{ marginTop: 4 }}>
                          Échéance : {formatFrDate(member.parentalClaimValidUntil)}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="info-item">
                          <label>Adresse :</label>
                          <span>{member.guardianEmail || '—'}</span>
                        </div>
                        <div className="info-item">
                          <label>Date de la demande :</label>
                          <span>{formatFrDate(member.legalRepresentativeConsentGivenAt)}</span>
                        </div>
                        {member.parentalClaimValidUntil ? (
                          <p className="no-badges">
                            Autorisation expirée le {formatFrDate(member.parentalClaimValidUntil)}.
                            {' '}Sans nouvelle validation, le rattachement n&apos;est pas effectif.
                          </p>
                        ) : (
                          <p className="no-badges">
                            Sans validation, le rattachement n&apos;est pas effectif.
                          </p>
                        )}
                        {consentMessage ? <p className="no-badges" style={{ color: '#1a7f37' }}>{consentMessage}</p> : null}
                        {consentError ? <p style={{ color: '#c0392b', fontSize: '0.85rem' }}>{consentError}</p> : null}
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          disabled={consentResending}
                          onClick={async () => {
                            setConsentResending(true);
                            setConsentMessage('');
                            setConsentError('');
                            try {
                              await resendCompanyGuardianAuthorization(companyId, member.id);
                              setConsentMessage("Demande d'autorisation renvoyée.");
                            } catch (e: any) {
                              setConsentError(e?.response?.data?.message || 'Envoi impossible.');
                            } finally {
                              setConsentResending(false);
                            }
                          }}
                        >
                          {consentResending ? 'Envoi…' : "Renvoyer la demande d'autorisation"}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {schoolId && isStudent() && (
                  <FoldableSection title="Représentant légal" isOpen>
                    <p className="no-badges" style={{ marginBottom: 8 }}>
                      L&apos;adresse du représentant légal, telle que l&apos;établissement la détient — jamais celle du compte Kinship d&apos;un parent rattaché.
                    </p>
                    {hasPendingGuardianEmail ? (
                      <>
                        <div className="info-item" style={{ alignItems: 'center' }}>
                          <label>Adresse actuelle:</label>
                          <span>{member.guardianEmail || '—'}</span>
                        </div>
                        <div className="info-item" style={{ alignItems: 'center' }}>
                          <label htmlFor={`guardian-email-${member.id}`}>Nouvelle adresse:</label>
                          <input
                            id={`guardian-email-${member.id}`}
                            type="email"
                            className="edit-input"
                            value={guardianEmailDraft}
                            onChange={(e) => setGuardianEmailDraft(e.target.value)}
                            placeholder="parent@exemple.fr"
                          />
                        </div>
                        <p className="no-badges" style={{ marginTop: 4, marginBottom: 0, fontSize: '0.85rem' }}>
                          L&apos;invitation partira à l&apos;adresse actuelle ; la nouvelle sera appliquée ensuite.
                        </p>
                      </>
                    ) : (
                      <div className="info-item" style={{ alignItems: 'center' }}>
                        <label htmlFor={`guardian-email-${member.id}`}>Email:</label>
                        <input
                          id={`guardian-email-${member.id}`}
                          type="email"
                          className="edit-input"
                          value={guardianEmailDraft}
                          onChange={(e) => setGuardianEmailDraft(e.target.value)}
                          placeholder="parent@exemple.fr"
                        />
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={guardianSaving}
                        onClick={() => void handleSaveGuardianEmail()}
                      >
                        {guardianSaving ? 'Enregistrement…' : 'Enregistrer'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={guardianInviting || !guardianDraftMatchesSaved}
                        title={
                          !guardianDraftMatchesSaved
                            ? 'Enregistrez d’abord la nouvelle adresse'
                            : undefined
                        }
                        onClick={() => void handleSendGuardianInvitation()}
                      >
                        {guardianInviting ? 'Envoi…' : inviteButtonLabel}
                      </button>
                    </div>
                    {guardianMessage ? (
                      <p style={{ color: '#057a55', fontSize: '0.85rem', marginTop: 8 }}>{guardianMessage}</p>
                    ) : null}
                    {guardianError ? (
                      <p style={{ color: '#c0392b', fontSize: '0.85rem', marginTop: 8 }}>{guardianError}</p>
                    ) : null}
                  </FoldableSection>
                )}

                {schoolId && isStudent() && (
                <FoldableSection title="Lien famille" isOpen>
                  {parentCodesLoading ? (
                    <p className="w-full text-center no-badges">Chargement…</p>
                  ) : (
                    <>
                      {parentCodesError ? (
                        <p style={{ color: '#c0392b', fontSize: '0.9rem' }}>{parentCodesError}</p>
                      ) : null}
                      {parentLinks.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                          {parentLinks.map((link) => (
                            <span
                              key={link.id}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                background: '#fce7f3',
                                color: '#db087c',
                                border: '1px solid #f9a8d4',
                                borderRadius: '999px',
                                padding: '4px 10px',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              {link.label}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="no-badges" style={{ marginBottom: '12px' }}>Aucun parent rattaché.</p>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {parentCodes.length === 0 ? (
                          <p className="no-badges">Aucun code émis pour cet établissement.</p>
                        ) : (
                          parentCodes.map((code) => (
                            <div
                              key={code.id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '8px 10px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '13px',
                              }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {code.status === 'unused' && code.code ? (
                                  <>
                                    <strong style={{ letterSpacing: '0.08em' }}>
                                      {revealedParentCodeIds.has(code.id) ? code.code : 'Code ••••••'}
                                    </strong>
                                    <button
                                      type="button"
                                      className="btn btn-outline btn-sm"
                                      style={{ padding: '2px 8px', fontSize: '11px' }}
                                      onClick={() => toggleParentCodeVisibility(code.id)}
                                    >
                                      {revealedParentCodeIds.has(code.id) ? 'Masquer' : 'Afficher'}
                                    </button>
                                  </>
                                ) : (
                                  <span style={{ color: '#6b7280' }}>Code masqué</span>
                                )}
                                <span style={{ color: '#6b7280' }}>
                                  · {code.status === 'unused' ? 'non utilisé' : code.status === 'used' ? 'utilisé' : 'invalidé'}
                                </span>
                              </span>
                              {code.issued_at ? (
                                <span style={{ color: '#9ca3af', fontSize: '11px' }}>
                                  {new Date(code.issued_at).toLocaleDateString('fr-FR')}
                                </span>
                              ) : null}
                            </div>
                          ))
                        )}
                      </div>
                      <div style={{ marginTop: '12px' }}>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          disabled={regeneratingCode}
                          onClick={() => void handleRegenerateParentCode()}
                          title="Invalide le code non utilisé et télécharge un nouveau coupon"
                        >
                          <i className="fas fa-redo"></i>
                          {regeneratingCode ? 'Régénération…' : 'Régénérer un code'}
                        </button>
                      </div>
                    </>
                  )}
                </FoldableSection>
                )}

                {schoolId && isStudent() && (
                  <FoldableSection title="Code élève" isOpen>
                    {studentLoginCodesLoading ? (
                      <p className="w-full text-center no-badges">Chargement…</p>
                    ) : (
                      <>
                        {studentLoginCodesError ? (
                          <p style={{ color: '#c0392b', fontSize: '0.9rem' }}>{studentLoginCodesError}</p>
                        ) : null}
                        {revealedStudentCode ? (
                          <p
                            style={{
                              background: '#ecfdf5',
                              border: '1px solid #a7f3d0',
                              borderRadius: 8,
                              padding: '10px 12px',
                              fontSize: 14,
                              marginBottom: 12,
                            }}
                          >
                            Nouveau code (affiché une seule fois) :{' '}
                            <strong style={{ letterSpacing: '0.08em' }}>{revealedStudentCode}</strong>
                          </p>
                        ) : null}
                        {(() => {
                          const hasActiveStudentCode = studentLoginCodes.some(
                            (c) => c.status === 'active'
                          );
                          return (
                            <>
                              {hasActiveStudentCode ? (
                                <p className="no-badges" style={{ marginBottom: 12 }}>
                                  Un code actif est en place. Régénérer invalide l’ancien immédiatement.
                                </p>
                              ) : null}
                              <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                disabled={regeneratingStudentCode}
                                onClick={() => void handleRegenerateStudentLoginCode()}
                                title={
                                  hasActiveStudentCode
                                    ? 'Invalide le code actif et affiche le nouveau une fois'
                                    : 'Génère un code élève et l’affiche une seule fois'
                                }
                              >
                                <i className="fas fa-redo"></i>
                                {regeneratingStudentCode
                                  ? hasActiveStudentCode
                                    ? 'Régénération…'
                                    : 'Génération…'
                                  : hasActiveStudentCode
                                    ? 'Régénérer le code élève'
                                    : 'Générer le code élève'}
                              </button>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </FoldableSection>
                )}

                {FEATURE_PIK_REMISE && schoolId && isStudent() && (
                  <FoldableSection
                    title="Clé personnelle"
                    isOpen={pikSectionOpen}
                    emptySuffix="compte activé"
                    collapsedContent={
                      <p className="no-badges" style={{ margin: 0 }}>
                        Cet élève a un compte activé — il remplace sa clé lui-même.
                      </p>
                    }
                  >
                    {pikLoading ? (
                      <p className="no-badges">Chargement…</p>
                    ) : (
                      <>
                        {pikError ? (
                          <p style={{ color: '#c0392b', fontSize: '0.85rem', marginBottom: 8 }}>{pikError}</p>
                        ) : null}
                        <div className="info-item">
                          <label>Clé remise :</label>
                          <span>{pikStatus?.remitted_label || 'JAMAIS'}</span>
                        </div>
                        <div className="info-item">
                          <label>Dernière impression :</label>
                          <span>{formatPikDate(pikStatus?.pik_last_printed_at)}</span>
                        </div>
                        {studentCanPrintKey && (
                          <div style={{ marginTop: 12 }}>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              disabled={pikPrinting}
                              onClick={() => void handlePrintPersonalKey()}
                            >
                              <i className="fas fa-print"></i>
                              {pikPrinting ? 'Impression…' : 'Imprimer une nouvelle clé'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </FoldableSection>
                )}

                {/* Compétences Section - hidden for members with temporary email */}
                {!member.hasTemporaryEmail && (() => {
                  const skillsToDisplay = isEditing ? editedMember.skills : member.skills;
                  const { mainSkills, subSkills } = organizeSkills(skillsToDisplay);
                  const hasSkills = mainSkills.length > 0 || subSkills.length > 0;
                  const useStudentFold = Boolean(schoolId && isStudent());
                  const skillsContent = (
                    <>
                      <div className="skills-grid">
                        {mainSkills.map((skill, index) => (
                          <span key={index} className="skill-tag">
                            {translateSkill(skill)}
                            {isEditing && (
                              <button
                                className="remove-tag-btn"
                                onClick={() => handleRemoveSkill(skill)}
                                title="Supprimer cette compétence"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            )}
                          </span>
                        ))}
                        {isEditing && (
                          <button className="add-tag-btn" onClick={handleAddSkill}>
                            <i className="fas fa-plus"></i>
                            Ajouter une compétence
                          </button>
                        )}
                      </div>
                      {subSkills.length > 0 && (
                        <>
                          <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Sous-compétences</h4>
                          <div className="skills-grid">
                            {subSkills.map((skill, index) => (
                              <span key={index} className="sub-skill-tag">
                                {translateSubSkill(skill)}
                                {isEditing && (
                                  <button
                                    className="remove-tag-btn"
                                    onClick={() => handleRemoveSkill(skill)}
                                    title="Supprimer cette sous-compétence"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  );

                  if (useStudentFold) {
                    return (
                      <FoldableSection title="Compétences personnelles renseignées" isOpen={hasSkills || isEditing}>
                        {skillsContent}
                      </FoldableSection>
                    );
                  }

                  return (
                    <div className="info-section">
                      <h3>Compétences</h3>
                      {!hasSkills ? (
                        <p className="w-full text-center no-badges">Aucune compétence renseignée</p>
                      ) : (
                        skillsContent
                      )}
                    </div>
                  );
                })()}

                {/* Disponibilités Section - hidden for members with temporary email */}
                {!member.hasTemporaryEmail && (() => {
                  const availabilityToDisplay = isEditing ? editedMember.availability : member.availability;
                  const hasAvailability = availabilityToDisplay.length > 0;
                  const useStudentFold = Boolean(schoolId && isStudent());
                  const availabilityContent = (
                    <div className="availability-grid">
                      {availabilityToDisplay.map((day, index) => (
                        <span key={index} className="availability-tag">
                          {day}
                          {isEditing && (
                            <button
                              className="remove-tag-btn"
                              onClick={() => handleRemoveAvailability(day)}
                              title="Supprimer cette disponibilité"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </span>
                      ))}
                      {isEditing && (
                        <button className="add-tag-btn" onClick={handleAddAvailability}>
                          <i className="fas fa-plus"></i>
                          Ajouter une disponibilité
                        </button>
                      )}
                    </div>
                  );

                  if (useStudentFold) {
                    return (
                      <FoldableSection title="Disponibilités" isOpen={hasAvailability || isEditing} emptySuffix="aucune renseignée">
                        {availabilityContent}
                      </FoldableSection>
                    );
                  }

                  return (
                    <div className="info-section">
                      <h3>Disponibilités</h3>
                      {!hasAvailability ? (
                        <p className="w-full text-center no-badges">Aucune disponibilité renseignée</p>
                      ) : (
                        availabilityContent
                      )}
                    </div>
                  );
                })()}

                {/* Services Section - only show for network members and not for temporary email */}
                {!member.hasTemporaryEmail && (member.take_trainee || member.propose_workshop) && (
                  <div className="info-section">
                    <h3>Services proposés</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {member.take_trainee && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '1.2rem' }}></i>
                          <span style={{ color: '#374151' }}>Accepte des stagiaires</span>
                        </div>
                      )}
                      {member.propose_workshop && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '1.2rem' }}></i>
                          <span style={{ color: '#374151' }}>Propose des ateliers</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Recent Preuves de compétences — D-LINK-SURFACES-01 §2.1bis / DoD ㉑ */}
                <div className="info-section">
                  <h3>3 dernières Preuves de compétences</h3>
                  {!member.latestBadges || member.latestBadges.length === 0 ? (
                    <p className="w-full text-center no-badges">Aucune Preuve de compétences</p>
                  ) : (
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      {member.latestBadges.slice(0, 3).map((latestBadge, index) => {
                        const badge = latestBadge.badge;
                        const badgeLevel = badge.level || 'level_1';
                        const isProgressBadge =
                          badge?.series &&
                          isSeriesWithCompetenceProgress(badge.series) &&
                          Array.isArray(badge.expertises) &&
                          Array.isArray((latestBadge as any).received_expertise_names);

                        if (isProgressBadge) {
                          const fullExpertiseNames = badge.expertises!;
                          const receivedExpertiseNames = (latestBadge as any).received_expertise_names as string[];
                          return (
                            <CompactProgressBadge
                              key={latestBadge.id || `${badge.id}-${badgeLevel}-${index}`}
                              badge={badge}
                              fullExpertiseNames={fullExpertiseNames}
                              receivedExpertiseNames={receivedExpertiseNames}
                            />
                          );
                        }

                        const badgeImage = badge.image_url ||
                          getLocalBadgeImage(badge.name, badgeLevel, badge.series) ||
                          '/TouKouLeur-Jaune.png';
                        return (
                          <img
                            key={latestBadge.id || `${badge.id}-${badgeLevel}-${index}`}
                            src={badgeImage}
                            alt={badge.name}
                            title={`${badge.name} - ${getBadgeLevelDisplayLabel(badge.series, badgeLevel)}`}
                            style={{
                              width: '48px',
                              height: '48px',
                              objectFit: 'contain',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb',
                              padding: '4px',
                              backgroundColor: '#f9fafb',
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Badges reçus section - hidden as not used */}
                {false && (
                <div className="info-section">
                  <h3>Preuves de compétences reçues</h3>
                  <div className="badges-grid">
                    {(() => {
                      // Get attributed badges for this member
                      const attributedBadges = state.badgeAttributions.filter(attribution =>
                        attribution.participantId === member.id
                      );

                      // Group existing badges by type and level, count duplicates
                      const badgeGroups: { [key: string]: { badge: Badge; count: number; isAttributed?: boolean; attribution?: any } } = {};

                      // Add existing badges
                      if (member.badges && member.badges.length > 0) {
                        member.badges.forEach(badgeId => {
                          const badge = mockBadges.find(b => b.id === badgeId);
                          if (badge) {
                            const key = `${badge.id}-${badge.level}`;
                            if (badgeGroups[key]) {
                              badgeGroups[key].count++;
                            } else {
                              badgeGroups[key] = { badge, count: 1 };
                            }
                          }
                        });
                      }

                      // Add attributed badges
                      attributedBadges.forEach(attribution => {
                        const key = `${attribution.badgeId}`;
                        if (badgeGroups[key]) {
                          badgeGroups[key].count++;
                        } else {
                          // Create a badge object from attribution data
                          const badge: Badge = {
                            id: attribution.badgeId,
                            name: attribution.badgeTitle,
                            description: getBadgeDescription(attribution.badgeTitle, attribution.badgeSeries, attribution.badgeLevel),
                            level: `Niveau ${attribution.badgeLevel} - ${getLevelName(attribution.badgeLevel)}`,
                            levelClass: `level-${attribution.badgeLevel}`,
                            icon: 'fas fa-award',
                            image: attribution.badgeImage,
                            category: attribution.badgeTitle,
                            series: attribution.badgeSeries.toLowerCase(),
                            recipients: 1,
                            created: new Date(attribution.dateAttribution).toLocaleDateString('fr-FR'),
                            domains: [attribution.domaineEngagement],
                            expertises: [],
                            recipients_list: [{
                              name: attribution.participantName,
                              avatar: attribution.participantAvatar,
                              date: new Date(attribution.dateAttribution).toLocaleDateString('fr-FR')
                            }],
                            files: attribution.preuve ? [{
                              name: attribution.preuve.name,
                              type: attribution.preuve.type,
                              size: attribution.preuve.size
                            }] : [],
                            requirements: [],
                            skills: []
                          };
                          badgeGroups[key] = {
                            badge,
                            count: 1,
                            isAttributed: true,
                            attribution
                          };
                        }
                      });

                      const badgeGroupsArray = Object.values(badgeGroups);
                      const BADGES_DISPLAY_LIMIT = 5; // Show only 5 badges initially
                      const displayedBadges = showAllBadgeGroups ? badgeGroupsArray : badgeGroupsArray.slice(0, BADGES_DISPLAY_LIMIT);
                      const hasMoreBadges = badgeGroupsArray.length > BADGES_DISPLAY_LIMIT;
                      const remainingCount = badgeGroupsArray.length - BADGES_DISPLAY_LIMIT;

                      if (badgeGroupsArray.length === 0) {
                        return <p className="no-badges">Aucune preuve de compétences attribuée</p>;
                      }

                      return (
                        <>
                          {displayedBadges.map((group, index) => (
                            <div key={index} className="badge-item">
                              <div className="badge-left-section">
                                <div className="badge-icon-container">
                                  <div className="badge-icon">
                                    <img src={group.badge.image} alt={group.badge.name} className="badge-image" />
                                  </div>
                                  {group.count > 1 && (
                                    <div className="badge-count">+{group.count - 1}</div>
                                  )}
                                </div>
                                <div className="badge-title-section">
                                  <h4 className="badge-name">{group.badge.name}</h4>
                                  <div className="badge-level-container">
                                    <span className={`badge-level ${group.badge.levelClass}`}>
                                      {group.badge.level}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="badge-info">
                                <p className="badge-serie">
                                  {group.badge.series.startsWith('Série ') || group.badge.series.startsWith('série ') ?
                                    group.badge.series.charAt(0).toUpperCase() + group.badge.series.slice(1) :
                                    `Série ${group.badge.series.charAt(0).toUpperCase() + group.badge.series.slice(1)}`}
                                </p>
                                <div className="badge-description-container">
                                  <p
                                    className={`badge-description ${expandedDescriptions[`${group.badge.id}-${group.badge.level}`] ? 'expanded' : ''}`}
                                  >
                                    {group.badge.description}
                                  </p>
                                  <button
                                    className="btn-expand-description"
                                    onClick={() => toggleDescriptionExpansion(`${group.badge.id}-${group.badge.level}`)}
                                  >
                                    {expandedDescriptions[`${group.badge.id}-${group.badge.level}`] ? 'Voir moins' : 'Voir plus'}
                                  </button>
                                </div>
                                {group.badge.files && group.badge.files.length > 0 && (
                                  <div className="badge-proof">
                                    <button
                                      className="btn-proof"
                                      onClick={() => {
                                        // Simulate file download
                                        const file = group.badge.files[0];
                                        const link = document.createElement('a');
                                        link.href = `#`; // In real app, this would be the actual file URL
                                        link.download = file.name;
                                        link.click();
                                      }}
                                    >
                                      <i className="fas fa-download"></i>
                                      Télécharger preuve
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          {hasMoreBadges && (
                            <div className="show-more-badges">
                              <button
                                className="btn-show-more"
                                onClick={() => setShowAllBadgeGroups(!showAllBadgeGroups)}
                              >
                                {showAllBadgeGroups ? (
                                  <>
                                    <i className="fas fa-chevron-up"></i>
                                    Voir moins ({remainingCount} badges cachés)
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-chevron-down"></i>
                                    Voir plus (+{remainingCount} badges)
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                )}

              </div>

              {/* Proposals Section — staff/company only; hidden for students and temporary email */}
              {!hideDeleteButton && !member.hasTemporaryEmail && !isStudent() && (
                <div className="info-section">
                  <h3>Propositions</h3>
                  <p className="section-subtitle">Ce que ce membre a indiqué dans ses paramètres personnels :</p>
                  <div className="proposals-grid">
                    <div className="permission-item">
                      <label className="permission-checkbox">
                        <input
                          disabled
                          type="checkbox"
                          checked={proposals.canProposeStage}
                          onChange={() => handleProposalChange('canProposeStage')}
                        />
                        <span className="checkmark"></span>
                        <span className="permission-label">Propose un stage</span>
                      </label>
                    </div>
                    <div className="permission-item">
                      <label className="permission-checkbox">
                        <input
                          disabled
                          type="checkbox"
                          checked={proposals.canProposeAtelier}
                          onChange={() => handleProposalChange('canProposeAtelier')}
                        />
                        <span className="checkmark"></span>
                        <span className="permission-label">Propose un atelier pro</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={onClose}>
                Fermer
              </button>
              {/* {isEditing ? (
                <>
                  <button className="btn btn-outline" onClick={handleEditToggle}>
                    <i className="fas fa-times"></i>
                    Annuler
                  </button>
                  <button className="btn btn-primary" onClick={handleSave}>
                    <i className="fas fa-save"></i>
                    Sauvegarder
                  </button>
                </>
              ) : (
                !hideEditButton && (
                  <button className="btn btn-primary" onClick={handleEditToggle}>
                    <i className="fas fa-edit"></i>
                    Modifier
                  </button>
                )
              )} */}
            </div>
          </>
        )}
      </div>
    </div>
  );

};

export default MemberModal;
